import React, { Suspense, useState } from "react";
import { useUser, useFindMany, useGlobalAction } from "@gadgetinc/react";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../api";
import "./admin.css";

// Error Boundary Component for Admin Features
class AdminErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('Admin feature error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ color: '#ff4757', marginBottom: '1rem' }}>
            ⚠️ Feature temporarily unavailable
          </div>
          <div style={{ color: '#d1d5db', fontSize: '14px', marginBottom: '1rem' }}>
            {this.props.featureName || 'This feature'} encountered an error
          </div>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px'
            }}
          >
            Try Again
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// Feature Loading Component
function AdminFeatureLoader({ children, fallback = null }) {
  return (
    <Suspense fallback={
      fallback || (
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center',
          color: '#d1d5db'
        }}>
          <div>Loading...</div>
        </div>
      )
    }>
      {children}
    </Suspense>
  );
}

// Admin Statistics Component
function AdminStats() {
  const [retryKey, setRetryKey] = useState(0);
  
  // Data fetching hooks with error handling
  const [{ data: allUsers, error: usersError, fetching: usersFetching }] = useFindMany(api.user, {
    select: { id: true, roles: { key: true, name: true }, createdAt: true, approvalStatus: true }
  });

  const [{ data: activeAccessCodes, error: accessCodesError, fetching: accessCodesFetching }] = useFindMany(api.accessCode, {
    filter: { isActive: { equals: true } },
    select: { id: true, codeType: true, currentUses: true, maxUses: true }
  });

  const [{ data: pendingUsers, error: pendingUsersError, fetching: pendingUsersFetching }] = useFindMany(api.user, {
    filter: { approvalStatus: { equals: "pending" } },
    select: { id: true, email: true, firstName: true, lastName: true }
  });

  // If there are errors, show fallback
  if (usersError || accessCodesError || pendingUsersError) {
    return (
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '1.5rem',
          color: '#ffffff'
        }}>Overview Statistics</h2>
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #ff4757',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ color: '#ff4757', marginBottom: '1rem' }}>
            Unable to load statistics
          </div>
          <button
            onClick={() => setRetryKey(prev => prev + 1)}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  // Calculate statistics with safe defaults
  const totalUsers = allUsers?.length || 0;
  const activeAccessCodesCount = activeAccessCodes?.length || 0;
  const pendingUsersCount = pendingUsers?.length || 0;
  
  // Today's signups
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todaysSignups = allUsers?.filter(user => {
    const userCreated = new Date(user.createdAt);
    return userCreated >= today;
  }).length || 0;

  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2 style={{
        fontSize: '20px',
        fontWeight: '600',
        marginBottom: '1.5rem',
        color: '#ffffff'
      }}>Overview Statistics</h2>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '1rem',
        marginBottom: '2rem'
      }}>
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          transition: 'all 0.2s ease'
        }}>
          <h3 style={{ 
            fontSize: '14px',
            color: '#d1d5db',
            marginBottom: '0.5rem',
            fontWeight: '500'
          }}>Total Users</h3>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#3b82f6'
          }}>
            {usersFetching ? '...' : totalUsers}
          </div>
        </div>
        
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          transition: 'all 0.2s ease'
        }}>
          <h3 style={{ 
            fontSize: '14px',
            color: '#d1d5db',
            marginBottom: '0.5rem',
            fontWeight: '500'
          }}>Active Access Codes</h3>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#3b82f6'
          }}>
            {accessCodesFetching ? '...' : activeAccessCodesCount}
          </div>
        </div>
        
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          transition: 'all 0.2s ease'
        }}>
          <h3 style={{ 
            fontSize: '14px',
            color: '#d1d5db',
            marginBottom: '0.5rem',
            fontWeight: '500'
          }}>Pending Approvals</h3>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#ff4757'
          }}>
            {pendingUsersFetching ? '...' : pendingUsersCount}
          </div>
        </div>
        
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '1.5rem',
          transition: 'all 0.2s ease'
        }}>
          <h3 style={{ 
            fontSize: '14px',
            color: '#d1d5db',
            marginBottom: '0.5rem',
            fontWeight: '500'
          }}>Today's Signups</h3>
          <div style={{
            fontSize: '16px',
            fontWeight: '700',
            color: '#10b981'
          }}>
            {usersFetching ? '...' : todaysSignups}
          </div>
        </div>
      </div>
    </section>
  );
}

// Admin Quick Actions Component
function AdminQuickActions({ pendingUsersCount = 0 }) {
  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2 style={{
        fontSize: '20px',
        fontWeight: '600',
        marginBottom: '1.5rem',
        color: '#ffffff'
      }}>Quick Actions</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
        <Link 
          to="/admin/access-codes" 
          style={{ 
            background: '#007bff', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
        >
          Manage Access Codes
        </Link>
        
        <Link 
          to="/admin/users" 
          style={{ 
            background: '#28a745', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#1e7e34'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
        >
          Manage Users
        </Link>
        
        <Link 
          to="/admin/institutions" 
          style={{ 
            background: '#64748b', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#475569'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#64748b'}
        >
          <div>Institution Management</div>
          <div style={{
            fontSize: '12px',
            opacity: '0.9',
            marginTop: '4px'
          }}>Config, users & settings</div>
        </Link>
          
        <Link 
          to="/admin/clinical-sites" 
          style={{ 
            background: '#dc2626', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
        >
          Clinical Sites
        </Link>
          
        <Link 
          to="/admin/invites" 
          style={{ 
            background: '#fd7e14', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#e8650e'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#fd7e14'}
        >
          Send Invites
        </Link>
        
        <Link 
          to="/admin/user-approvals" 
          style={{ 
            background: '#ffc107', 
            color: '#212529', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#e0a800'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#ffc107'}
        >
          Approve Users ({pendingUsersCount})
        </Link>
        
        <Link 
          to="/admin/analytics" 
          style={{ 
            background: '#6f42c1', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#5a32a3'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#6f42c1'}
        >
          View Analytics
        </Link>
        
        <Link 
          to="/admin/assistants" 
          style={{ 
            background: '#17a2b8', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#117a8b'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#17a2b8'}
        >
          Manage Assistants
        </Link>
        
        <Link 
          to="/admin/embed-tokens" 
          style={{ 
            background: '#dc3545', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#bd2130'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
        >
          Embed Tokens
        </Link>
        
        <Link 
          to="/admin/fine-tuning" 
          style={{ 
            background: '#8b5cf6', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#7c3aed'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#8b5cf6'}
        >
          Fine-Tuning
        </Link>
        
        <Link 
          to="/admin/agent-fine-tuning" 
          style={{ 
            background: '#f59e0b', 
            color: 'white', 
            padding: '1rem', 
            borderRadius: '8px', 
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#d97706'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#f59e0b'}
        >
          Agent Fine-Tuning
        </Link>

        <Link
          to="/admin/calendar-sync"
          style={{
            background: '#0ea5e9',
            color: 'white',
            padding: '1rem',
            borderRadius: '8px',
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#0284c7'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#0ea5e9'}
        >
          Calendar Sync
        </Link>

        <Link
          to="/shift-compliance"
          style={{
            background: '#14b8a6',
            color: 'white',
            padding: '1rem',
            borderRadius: '8px',
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#0d9488'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#14b8a6'}
        >
          Shift Compliance
        </Link>

        <Link
          to="/admin/shift-packs"
          style={{
            background: '#a855f7',
            color: 'white',
            padding: '1rem',
            borderRadius: '8px',
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#9333ea'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#a855f7'}
        >
          Shift Packs
        </Link>

        <Link
          to="/admin/sim-sessions"
          style={{
            background: '#ec4899',
            color: 'white',
            padding: '1rem',
            borderRadius: '8px',
            textDecoration: 'none',
            textAlign: 'center',
            fontWeight: '500',
            transition: 'background-color 0.2s'
          }}
          onMouseOver={(e) => e.target.style.backgroundColor = '#db2777'}
          onMouseOut={(e) => e.target.style.backgroundColor = '#ec4899'}
        >
          Sim Sessions
        </Link>
      </div>
    </section>
  );
}

// Admin Recent Activity Component
function AdminRecentActivity() {
  const [retryKey, setRetryKey] = useState(0);
  
  const [{ data: recentUsers, error: recentUsersError, fetching: recentUsersFetching }] = useFindMany(api.user, {
    sort: { createdAt: "Descending" },
    first: 5,
    select: { id: true, email: true, firstName: true, lastName: true, createdAt: true, roles: { key: true, name: true } }
  });

  // Error fallback
  if (recentUsersError) {
    return (
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '1.5rem',
          color: '#ffffff'
        }}>Recent Activity</h2>
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #ff4757',
          borderRadius: '12px',
          padding: '1.5rem',
          textAlign: 'center'
        }}>
          <div style={{ color: '#ff4757', marginBottom: '1rem' }}>
            Unable to load recent activity
          </div>
          <button
            onClick={() => setRetryKey(prev => prev + 1)}
            style={{
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '6px',
              cursor: 'pointer'
            }}
          >
            Retry
          </button>
        </div>
      </section>
    );
  }

  return (
    <section style={{ marginBottom: '2rem' }}>
      <h2 style={{
        fontSize: '20px',
        fontWeight: '600',
        marginBottom: '1.5rem',
        color: '#ffffff'
      }}>Recent Activity</h2>
      <div style={{
        background: '#1e1e1e',
        border: '1px solid #333',
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '1rem 1.5rem',
          borderBottom: '1px solid #333',
          background: '#252525'
        }}>
          <h3 style={{
            fontSize: '16px',
            fontWeight: '600',
            margin: '0',
            color: '#ffffff'
          }}>Recent User Registrations</h3>
        </div>
        <div style={{ padding: '1.5rem' }}>
          {recentUsersFetching ? (
            <div style={{
              color: '#d1d5db',
              textAlign: 'center',
              padding: '1rem'
            }}>Loading recent users...</div>
          ) : recentUsers && recentUsers.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {recentUsers.map((user) => {
                // Build display name with fallback logic
                let displayName = '';
                if (user.firstName && user.lastName) {
                  displayName = `${user.firstName} ${user.lastName}`;
                } else if (user.firstName) {
                  displayName = user.firstName;
                } else if (user.lastName) {
                  displayName = user.lastName;
                } else {
                  displayName = user.email;
                }
                
                return (
                  <div key={user.id} style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    padding: '1rem',
                    background: '#2a2a2a',
                    borderRadius: '8px',
                    border: '1px solid #404040',
                    transition: 'all 0.2s ease'
                  }}>
                    <div>
                      <span style={{
                        color: '#ffffff',
                        fontWeight: '500',
                        marginBottom: '0.25rem',
                        display: 'block'
                      }}>
                        {displayName}
                      </span>
                      {displayName !== user.email && (
                        <div style={{
                          fontSize: '14px',
                          color: '#d1d5db'
                        }}>
                          {user.email}
                        </div>
                      )}
                    </div>
                    <div style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      whiteSpace: 'nowrap',
                      marginLeft: '1rem'
                    }}>
                      {new Date(user.createdAt).toLocaleDateString()}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{
              color: '#d1d5db',
              textAlign: 'center',
              padding: '2rem',
              fontSize: '16px'
            }}>No recent registrations</div>
          )}
        </div>
      </div>
    </section>
  );
}

// Helper function to check if user has admin privileges
function checkIsAdmin(user) {
  if (!user) return false;
  
  // Special email check for backwards compatibility
  if (user?.email === '74spartan@gmail.com') {
    return true;
  }
  
  // Check if user has roles
  if (!user?.roles || !Array.isArray(user.roles)) {
    return false;
  }
  
  // Define admin role names to check for
  const adminRoleNames = ['admin', 'sysadmin', 'system-admin'];
  
  // Check roles - handle both string and object formats
  return user.roles.some(role => {
    if (typeof role === 'string') {
      // Role is a string, check directly
      return adminRoleNames.includes(role);
    } else if (typeof role === 'object' && role !== null) {
      // Role is an object, check key and name properties
      return adminRoleNames.includes(role.key) || adminRoleNames.includes(role.name);
    }
    return false;
  });
}

export default function AdminDashboard() {
  const user = useUser(api);
  const navigate = useNavigate();

  // Debug logging for roles
  React.useEffect(() => {
    if (user) {
      console.log('Current user roles:', user.roles);
      console.log('User email:', user.email);
      console.log('Role types:', user.roles?.map(role => typeof role));
    }
  }, [user]);

  // Authentication check using helper function
  const isAdmin = checkIsAdmin(user);

  // Redirect if not authenticated
  React.useEffect(() => {
    if (user && !isAdmin) {
      console.log('Access denied - user roles:', user.roles);
      navigate('/sign-in');
    }
  }, [user, isAdmin, navigate]);

  // Data fetching hooks
  const [{ data: allUsers, error: usersError, fetching: usersFetching }] = useFindMany(api.user, {
    select: { id: true, roles: { key: true, name: true }, createdAt: true, approvalStatus: true }
  });

  const [{ data: activeAccessCodes, error: accessCodesError, fetching: accessCodesFetching }] = useFindMany(api.accessCode, {
    filter: { isActive: { equals: true } },
    select: { id: true, codeType: true, currentUses: true, maxUses: true }
  });

  const [{ data: recentUsers, error: recentUsersError, fetching: recentUsersFetching }] = useFindMany(api.user, {
    sort: { createdAt: "Descending" },
    first: 5,
    select: { id: true, email: true, firstName: true, lastName: true, createdAt: true, roles: { key: true, name: true } }
  });

  const [{ data: pendingUsers, error: pendingUsersError, fetching: pendingUsersFetching }] = useFindMany(api.user, {
    filter: { approvalStatus: { equals: "pending" } },
    select: { id: true, email: true, firstName: true, lastName: true }
  });

  // Debug logging for user counting
  React.useEffect(() => {
    console.log('All users data:', allUsers);
    console.log('All users length:', allUsers?.length);
    console.log('Users fetching:', usersFetching);
    console.log('Users error:', usersError);
  }, [allUsers, usersFetching, usersError]);

  // Loading state
  if (!user || usersFetching) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '18px'
      }}>
        <div>Loading...</div>
      </div>
    );
  }

  // Unauthorized access
  if (!isAdmin) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#111111',
        color: '#ffffff',
        padding: '2rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          padding: '2rem',
          maxWidth: '500px',
          width: '100%'
        }}>
          <h2 style={{ color: '#ff4757', marginBottom: '1rem' }}>Access Denied</h2>
          <p style={{ marginBottom: '1.5rem' }}>You need administrator privileges to access this page.</p>
          <p style={{ fontWeight: 'bold', marginBottom: '1rem' }}>Debug Info:</p>
          <div style={{
            background: '#0a0a0a',
            padding: '1rem',
            borderRadius: '8px',
            marginBottom: '1.5rem',
            fontSize: '14px'
          }}>
            <p style={{ marginBottom: '0.5rem' }}><strong>Email:</strong> {user?.email || 'Not available'}</p>
            <p style={{ marginBottom: '0.5rem' }}><strong>Roles:</strong> {user?.roles ? JSON.stringify(user.roles, null, 2) : 'No roles'}</p>
            <p><strong>Required:</strong> admin, system-admin roles or email 74spartan@gmail.com</p>
          </div>
          <Link 
            to="/sign-in" 
            style={{
              display: 'inline-block',
              background: '#3b82f6',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '500',
              transition: 'all 0.2s ease'
            }}
            onMouseOver={(e) => e.target.style.background = '#2563eb'}
            onMouseOut={(e) => e.target.style.background = '#3b82f6'}
          >
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  // Calculate statistics
  const totalUsers = allUsers?.length || 0;
  const activeAccessCodesCount = activeAccessCodes?.length || 0;
  const pendingUsersCount = pendingUsers?.length || 0;
  


  // Today's signups
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todaysSignups = allUsers?.filter(user => {
    const userCreated = new Date(user.createdAt);
    return userCreated >= today;
  }).length || 0;

  return (
    <div className="admin-container">
      <div className="admin-content">
        
        {/* Back to Assistants Button */}
        <div style={{ marginBottom: '2rem' }}>
          <Link 
            to="/" 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              background: '#3b82f6',
              color: 'white',
              padding: '12px 24px',
              borderRadius: '8px',
              textDecoration: 'none',
              fontWeight: '600',
              fontSize: '16px',
              border: 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)'
            }}
            onMouseOver={(e) => {
              e.target.style.background = '#2563eb';
              e.target.style.transform = 'translateY(-1px)';
              e.target.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';
            }}
            onMouseOut={(e) => {
              e.target.style.background = '#3b82f6';
              e.target.style.transform = 'translateY(0)';
              e.target.style.boxShadow = '0 2px 4px rgba(0, 0, 0, 0.1)';
            }}
          >
            <span style={{ fontSize: '18px' }}>←</span>
            Back to Assistants
          </Link>
        </div>
      
      {/* Overview Stats Section */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '1.5rem',
          color: '#ffffff'
        }}>Overview Statistics</h2>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1rem',
          marginBottom: '2rem'
        }}>
          <div style={{
            background: '#1e1e1e',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem',
            transition: 'all 0.2s ease'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              color: '#d1d5db',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>Total Users</h3>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#3b82f6'
            }}>{totalUsers}</div>
          </div>
          
          <div style={{
            background: '#1e1e1e',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem',
            transition: 'all 0.2s ease'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              color: '#d1d5db',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>Active Access Codes</h3>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#3b82f6'
            }}>{activeAccessCodesCount}</div>
          </div>
          
          <div style={{
            background: '#1e1e1e',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem',
            transition: 'all 0.2s ease'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              color: '#d1d5db',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>Pending Approvals</h3>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#ff4757'
            }}>{pendingUsersCount}</div>
          </div>
          
          <div style={{
            background: '#1e1e1e',
            border: '1px solid #333',
            borderRadius: '12px',
            padding: '1.5rem',
            transition: 'all 0.2s ease'
          }}>
            <h3 style={{ 
              fontSize: '14px',
              color: '#d1d5db',
              marginBottom: '0.5rem',
              fontWeight: '500'
            }}>Today's Signups</h3>
            <div style={{
              fontSize: '16px',
              fontWeight: '700',
              color: '#10b981'
            }}>{todaysSignups}</div>
          </div>
        </div>
      </section>

      {/* User Management Section */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          marginBottom: '1rem',
          color: '#ffffff',
          borderBottom: '2px solid #3b82f6',
          paddingBottom: '0.5rem'
        }}>User Management</h2>
        <p style={{
          color: '#d1d5db',
          marginBottom: '1.5rem',
          fontSize: '14px'
        }}>Manage user accounts, approvals, invitations, and access controls</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <Link 
            to={pendingUsersCount > 0 ? "/admin/users?status=pending" : "/admin/users"}
            style={{ 
              background: '#28a745', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s',
              position: 'relative'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#1e7e34'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#28a745'}
          >
            <div>Manage Users</div>
            <div style={{
              fontSize: '12px',
              opacity: '0.9',
              marginTop: '4px'
            }}>View and approve users</div>
            {pendingUsersCount > 0 && (
              <span style={{
                position: 'absolute',
                top: '-8px',
                right: '-8px',
                background: '#dc3545',
                color: 'white',
                borderRadius: '50%',
                padding: '4px 8px',
                fontSize: '12px',
                fontWeight: 'bold',
                minWidth: '20px',
                textAlign: 'center'
              }}>
                {pendingUsersCount}
              </span>
            )}
          </Link>
          
          <Link 
            to="/admin/invite" 
            style={{ 
              background: '#fd7e14', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#e8650e'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#fd7e14'}
          >
            Send Invites
          </Link>
          
          <Link 
            to="/admin/access-codes" 
            style={{ 
              background: '#007bff', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0056b3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#007bff'}
          >
            Access Codes
          </Link>
        </div>
      </section>

      {/* Institution Management Section */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          marginBottom: '1rem',
          color: '#ffffff',
          borderBottom: '2px solid #10b981',
          paddingBottom: '0.5rem'
        }}>Institution Management</h2>
        <p style={{
          color: '#d1d5db',
          marginBottom: '1.5rem',
          fontSize: '14px'
        }}>Complete institution management: configuration, users, cohorts, clinical sites, and educational partnerships</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <Link 
            to="/admin/institutions" 
            style={{ 
              background: '#64748b', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#475569'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#64748b'}
          >
            <div>Institutions</div>
            <div style={{
              fontSize: '12px',
              opacity: '0.9',
              marginTop: '4px'
            }}>Config, users & cohorts</div>
          </Link>
          
          <Link 
            to="/admin/clinical-sites" 
            style={{ 
              background: '#dc2626', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#b91c1c'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#dc2626'}
          >
            Clinical Sites
          </Link>
          
          <Link 
            to="/admin/preceptors" 
            style={{ 
              background: '#7c2d12', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#6b2516'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#7c2d12'}
          >
            Preceptors
          </Link>

          <Link 
            to="/admin/institutional-partnerships" 
            style={{ 
              background: '#6366f1', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4f46e5'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6366f1'}
          >
            Institutional Partnerships
          </Link>
        </div>
      </section>

      {/* Document Processing & AI Section */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          marginBottom: '1rem',
          color: '#ffffff',
          borderBottom: '2px solid #f59e0b',
          paddingBottom: '0.5rem'
        }}>Document Processing & AI</h2>
        <p style={{
          color: '#d1d5db',
          marginBottom: '1.5rem',
          fontSize: '14px'
        }}>AI-powered document analysis, review workflows, and intelligent agent management</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <Link 
            to="/admin/clinical-docs" 
            style={{ 
              background: '#e11d48', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#be185d'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#e11d48'}
          >
            Clinical Document Processing
          </Link>

          <Link 
            to="/admin/hitl-queue" 
            style={{ 
              background: '#dc3545',
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s',
              position: 'relative',
              border: '2px solid #ffc107'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#c82333'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
          >
            <div>HITL Review Queue</div>
            <div style={{
              fontSize: '11px',
              opacity: '0.9',
              marginTop: '2px'
            }}>Human-in-the-Loop Reviews</div>
            <span style={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              background: '#ffc107',
              color: '#000',
              borderRadius: '4px',
              padding: '2px 6px',
              fontSize: '10px',
              fontWeight: 'bold'
            }}>
              NEW
            </span>
          </Link>

          <Link 
            to="/ai-system-generator" 
            style={{ 
              background: '#8b5cf6', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#7c3aed'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#8b5cf6'}
          >
            AI System Generator
          </Link>
          
          <Link 
            to="/admin/agent-control-panel" 
            style={{ 
              background: '#10b981', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#059669'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#10b981'}
          >
            Agent Management
          </Link>

          <Link 
            to="/admin/agent-chat" 
            style={{ 
              background: '#0ea5e9', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0284c7'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#0ea5e9'}
          >
            Agent Chat Interface
          </Link>

          <Link 
            to="/admin/agents-v2" 
            style={{ 
              background: '#8b5cf6', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s',
              position: 'relative',
              border: '2px solid #fbbf24'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#7c3aed'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#8b5cf6'}
          >
            <div>Agents V2 (Beta)</div>
            <div style={{
              fontSize: '11px',
              opacity: '0.9',
              marginTop: '2px'
            }}>Next Generation Agent System</div>
            <span style={{
              position: 'absolute',
              top: '-6px',
              right: '-6px',
              background: '#fbbf24',
              color: '#000',
              borderRadius: '4px',
              padding: '2px 6px',
              fontSize: '10px',
              fontWeight: 'bold'
            }}>
              BETA
            </span>
          </Link>
          
          <Link 
            to="/test-clinical-docs" 
            style={{ 
              background: '#06b6d4', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#0891b2'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#06b6d4'}
          >
            🧪 Test Clinical Docs
          </Link>
        </div>
      </section>

      {/* System Features Section */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          marginBottom: '1rem',
          color: '#ffffff',
          borderBottom: '2px solid #8b5cf6',
          paddingBottom: '0.5rem'
        }}>System Features</h2>
        <p style={{
          color: '#d1d5db',
          marginBottom: '1.5rem',
          fontSize: '14px'
        }}>Core system functionality, analytics, and AI model management</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <Link 
            to="/admin/analytics" 
            style={{ 
              background: '#6f42c1', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5a32a3'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6f42c1'}
          >
            Analytics
          </Link>
          
          <Link 
            to="/admin/assistants" 
            style={{ 
              background: '#17a2b8', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#117a8b'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#17a2b8'}
          >
            Assistants
          </Link>
          
          <Link 
            to="/admin/fine-tuning" 
            style={{ 
              background: '#8b5cf6', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#7c3aed'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#8b5cf6'}
          >
            Fine-Tuning
          </Link>
          
          <Link 
            to="/admin/agent-fine-tuning" 
            style={{ 
              background: '#f59e0b', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#d97706'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#f59e0b'}
          >
            Agent Fine-Tuning
          </Link>
          
          <Link 
            to="/admin/embed-tokens" 
            style={{ 
              background: '#dc3545', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#bd2130'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#dc3545'}
          >
            Embed Tokens
          </Link>
        </div>
      </section>

      {/* Advanced Tools Section */}
      <section style={{ marginBottom: '3rem' }}>
        <h2 style={{
          fontSize: '24px',
          fontWeight: '700',
          marginBottom: '1rem',
          color: '#ffffff',
          borderBottom: '2px solid #dc3545',
          paddingBottom: '0.5rem'
        }}>Advanced Tools</h2>
        <p style={{
          color: '#d1d5db',
          marginBottom: '1.5rem',
          fontSize: '14px'
        }}>Development and testing tools for system administration</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem' }}>
          <Link 
            to="/admin/vm-agents" 
            style={{ 
              background: '#6366f1', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#4f46e5'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6366f1'}
          >
            VM Agents
          </Link>
          
          <Link 
            to="/admin/file-manager" 
            style={{ 
              background: '#059669', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#047857'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#059669'}
          >
            File Manager
          </Link>
          
          <Link 
            to="/admin/voice-agent-config" 
            style={{ 
              background: '#7c3aed', 
              color: 'white', 
              padding: '1rem', 
              borderRadius: '8px', 
              textDecoration: 'none',
              textAlign: 'center',
              fontWeight: '500',
              transition: 'background-color 0.2s'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#6d28d9'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#7c3aed'}
          >
            Voice Agent Config
          </Link>
        </div>
      </section>

      {/* Recent Activity Section */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{
          fontSize: '20px',
          fontWeight: '600',
          marginBottom: '1.5rem',
          color: '#ffffff'
        }}>Recent Activity</h2>
        <div style={{
          background: '#1e1e1e',
          border: '1px solid #333',
          borderRadius: '12px',
          overflow: 'hidden'
        }}>
          <div style={{
            padding: '1rem 1.5rem',
            borderBottom: '1px solid #333',
            background: '#252525'
          }}>
            <h3 style={{
              fontSize: '16px',
              fontWeight: '600',
              margin: '0',
              color: '#ffffff'
            }}>Recent User Registrations</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {recentUsersFetching ? (
              <div style={{
                color: '#d1d5db',
                textAlign: 'center',
                padding: '1rem'
              }}>Loading recent users...</div>
            ) : recentUsers && recentUsers.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                {recentUsers.map((user) => {
                  // Build display name with fallback logic
                  let displayName = '';
                  if (user.firstName && user.lastName) {
                    displayName = `${user.firstName} ${user.lastName}`;
                  } else if (user.firstName) {
                    displayName = user.firstName;
                  } else if (user.lastName) {
                    displayName = user.lastName;
                  } else {
                    displayName = user.email;
                  }
                  
                  return (
                    <div key={user.id} style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      padding: '1rem',
                      background: '#2a2a2a',
                      borderRadius: '8px',
                      border: '1px solid #404040',
                      transition: 'all 0.2s ease'
                    }}>
                      <div>
                        <span style={{
                          color: '#ffffff',
                          fontWeight: '500',
                          marginBottom: '0.25rem',
                          display: 'block'
                        }}>
                          {displayName}
                        </span>
                        {displayName !== user.email && (
                          <div style={{
                            fontSize: '14px',
                            color: '#d1d5db'
                          }}>
                            {user.email}
                          </div>
                        )}
                      </div>
                      <div style={{
                        fontSize: '14px',
                        color: '#6b7280',
                        whiteSpace: 'nowrap',
                        marginLeft: '1rem'
                      }}>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{
                color: '#d1d5db',
                textAlign: 'center',
                padding: '2rem',
                fontSize: '16px'
              }}>No recent registrations</div>
            )}
          </div>
        </div>
      </section>
      </div>
    </div>
  );
}
